/**
 * Ses girisi (kayit) ve okuma (TTS).
 *
 * ## KURAL: ses ve metin hicbir dis servise gitmiyor
 *
 * Bu bir tercih degil, projenin var olma sebebi. Iki ayri tuzak var ve
 * ikisinin de cozumu farkli:
 *
 * **Giris.** `webkitSpeechRecognition` bedava ve sifir kaynak - ama Chrome
 * sesi GOOGLE SUNUCULARINA gonderiyor. Bu API HIC kullanilmiyor; yerine
 * `MediaRecorder` ile kaydediyoruz ve yalnizca KENDI sunucumuza POST ediyoruz
 * (`tests/unit/test_no_cloud_speech.py` bunu kod taramasiyla zorluyor).
 *
 * **Okuma.** `speechSynthesis` mesru bir API ama Edge/Chrome'daki
 * "natural"/"online" sesler metni MICROSOFT SUNUCULARINA gonderiyor. Burada
 * yasak API degil, FILTRE eksikligi tehlikeli: ses listesi
 * `voice.localService === true` ile filtreleniyor ve yerel ses yoksa TTS
 * KAPALI gosteriliyor. Sessizce buluta dusmek yasak - kullanici bilmeden
 * yanitlarini disariya gonderiyor olurdu.
 */

import { api } from './api'
import { SentenceSplitter } from './sentences'

/** Sunucunun bildirdigi yetenekler. */
export interface AudioCapabilities {
  stt: { enabled: boolean; reason: string | null; max_seconds: number }
  tts: {
    enabled: boolean
    require_local_voice: boolean
    /** Sunucu tarafi sentez var mi (macOS `say`, bkz. services/speech.py). */
    server?: boolean
    languages?: string[]
  }
}

/** `MediaRecorder` destekleniyor mu? Safari uzun sure desteklemedi. */
export function canRecord(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia)
  )
}

/**
 * Mikrofon acilamadiginda GERCEK sebebin ceviri anahtari.
 *
 * Onceki hal her hatada "Mikrofon izni verilmedi" diyordu. Gercek bir
 * vakada sebep bambaskaydi: sayfanin `Permissions-Policy` basligi mikrofonu
 * tamamen kapatiyordu ve tarayici kullaniciya HIC SORMADAN reddediyordu.
 * Kullanici "izin istenmedi ki" diye bakakaldi. Tarayicinin verdigi hata
 * adi sebebi soyluyor; onu kullaniciya ceviriyoruz.
 */
export function micErrorKey(error: unknown): string {
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return 'voice.mic.insecure'
  }
  const name = error instanceof DOMException || error instanceof Error ? error.name : ''
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'voice.mic.denied'
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'voice.mic.noDevice'
    case 'NotReadableError':
    case 'AbortError':
      return 'voice.mic.busy'
    default:
      return 'voice.mic.unavailable'
  }
}

export interface Recording {
  /** Kaydi bitirir ve toplanan sesi dondurur. */
  stop(): Promise<Blob>
  /** Kaydi iptal eder; mikrofon SERBEST birakilir. */
  cancel(): void
}

/**
 * Mikrofon kaydini baslatir.
 *
 * Mikrofon izni burada isteniyor, uygulama acilisinda DEGIL: acilista izin
 * istemek reddedilmenin en hizli yolu ve reddedilen bir izni geri almak
 * kullanici icin zahmetli.
 */
export async function startRecording(): Promise<Recording> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  const chunks: Blob[] = []

  // Bicimi TARAYICI seciyor. Sabit bir mime dayatmak Safari'de kaydi
  // tamamen kiriyordu; sunucu tarafi zaten birkac bicim kabul ediyor.
  const recorder = new MediaRecorder(stream)
  recorder.addEventListener('dataavailable', (event) => {
    if (event.data.size > 0) chunks.push(event.data)
  })
  recorder.start()

  // Mikrofon izin gostergesi (tarayici sekmesindeki kirmizi nokta) yalnizca
  // TRACK'LER kapatildiginda kayboluyor; recorder'i durdurmak yetmiyor.
  // Kapatmayi atlamak kullaniciya "hala dinleniyor" gostermek demek.
  const release = () => stream.getTracks().forEach((track) => track.stop())

  return {
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.addEventListener('stop', () => {
          release()
          resolve(new Blob(chunks, { type: recorder.mimeType || 'audio/webm' }))
        })
        recorder.stop()
      }),
    cancel: () => {
      try {
        if (recorder.state !== 'inactive') recorder.stop()
      } finally {
        release()
      }
    },
  }
}

/** Kayit uzantisini mime'dan cikarir - sunucu uzanti allowlist'i uyguluyor. */
export function extensionFor(mimeType: string): string {
  if (mimeType.includes('ogg')) return 'ogg'
  if (mimeType.includes('mp4') || mimeType.includes('m4a')) return 'm4a'
  if (mimeType.includes('wav')) return 'wav'
  return 'webm'
}

// ------------------------------------------------------------------- okuma

/**
 * Okunabilir sesler - YALNIZCA yerel olanlar.
 *
 * `localService === false` olan sesler metni bulut servisine gonderiyor.
 * Filtre `requireLocal` ile kapatilabiliyor ama sunucu varsayilani `true` ve
 * oyle kalmali.
 */
export function availableVoices(requireLocal: boolean): SpeechSynthesisVoice[] {
  if (typeof speechSynthesis === 'undefined') return []
  const voices = speechSynthesis.getVoices()
  if (!requireLocal) return voices
  return voices.filter((voice) => voice.localService)
}

/** Turkce yerel ses varsa onu, yoksa ilk yerel sesi dondurur. */
export function preferredVoice(requireLocal: boolean): SpeechSynthesisVoice | null {
  const voices = availableVoices(requireLocal)
  return voices.find((voice) => voice.lang.toLowerCase().startsWith('tr')) ?? voices[0] ?? null
}

/**
 * Ses listesinin YUKLENMESINI bekler.
 *
 * ## Gercek ariza
 *
 * Chrome/Edge'de `speechSynthesis.getVoices()` sayfa acilir acilmaz
 * cagrilirsa BOS dizi donduruyor - motor ses listesini ASENKRON yukluyor ve
 * hazir oldugunda `voiceschanged` olayini atesliyor. Sonuc: kullanici ilk
 * tikladiginda "yerel ses bulunamadi" goruyor, hemen ardindan (liste artik
 * yuklenmis oldugu icin) ikinci tiklamada calisiyor - yerel ses hic yoktan
 * degil, henuz YUKLENMEMISTEN.
 *
 * `getVoices()`in KENDISI de yukleme surecini tetikliyor bazi tarayicilarda,
 * o yuzden asagida once bir kez cagriliyor.
 *
 * ## Guvenlik agi: sabit zaman asimi
 *
 * Bazi tarayicilarda (gercekten ses yoksa) `voiceschanged` HIC atesLenmiyor.
 * Olay olmadan sonsuza kadar beklemek butonu kalici olarak tepkisiz
 * birakirdi.
 */
export function voicesReady(): Promise<void> {
  if (typeof speechSynthesis === 'undefined') return Promise.resolve()
  if (speechSynthesis.getVoices().length > 0) return Promise.resolve()

  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      speechSynthesis.removeEventListener('voiceschanged', finish)
      resolve()
    }
    speechSynthesis.addEventListener('voiceschanged', finish)
    setTimeout(finish, 1000)
  })
}

export interface SpeakResult {
  ok: boolean
  /** Okunamadiysa SEBEBI - kullaniciya soylenmek uzere. */
  reason?: 'unsupported' | 'no_local_voice'
}

/**
 * Metni okur.
 *
 * Yerel ses YOKSA okumuyor ve sebebini donduruyor. Buluta dusmek yerine
 * sessiz kalmak da yanlis olurdu: kullanici butona basiyor, hicbir sey
 * olmuyor, ve neden oldugunu bilmiyor.
 *
 * `onEnd`, okuma DOGAL olarak bitince veya hata alinca cagriliyor - cagiran
 * bunu "okunuyor" gostergesini kapatmak icin kullaniyor. Olmadan gosterge
 * yalnizca kullanici KENDISI durdurunca kapaniyordu; ses kendiliginden
 * bitince duraklat butonu SONSUZA KADAR "durdur" gorunuyordu.
 */
export function speak(text: string, requireLocal: boolean, onEnd?: () => void): SpeakResult {
  if (typeof speechSynthesis === 'undefined') return { ok: false, reason: 'unsupported' }

  const voice = preferredVoice(requireLocal)
  if (voice === null) return { ok: false, reason: 'no_local_voice' }

  // Onceki okumayi kes: iki yanitin ust uste okunmasi anlasilmaz bir
  // gurultu uretiyor.
  speechSynthesis.cancel()

  const utterance = new SpeechSynthesisUtterance(text)
  utterance.voice = voice
  utterance.lang = voice.lang
  if (onEnd) {
    utterance.onend = onEnd
    // Hata da bir BITIS: aksi halde ortada kesilen bir okuma butonu
    // kalici olarak "durdur" gosterirdi.
    utterance.onerror = onEnd
  }
  speechSynthesis.speak(utterance)
  return { ok: true }
}

export function stopSpeaking(): void {
  if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel()
}

// --- Sunucu tarafi okuma --------------------------------------------------------
//
// Tarayicinin `SpeechSynthesis`i yerine SUNUCUNUN urettigi ses. Neden:
//   * kalite ve dil kapsami tarayiciya gore degismiyor,
//   * "natural" tarayici sesleri metni Microsoft'a gonderiyordu - sunucu
//     sentezi metni makineden CIKARMIYOR,
//   * ayni uc API anahtariyla da calisiyor.
// Sunucu sentezi yoksa (kapali/kurulu dil yok) cagiran tarayiciya dusuyor.

let current: HTMLAudioElement | null = null
let currentUrl: string | null = null

// TEK ses ogesi, tekrar kullaniliyor. Neden: tarayicilarin otomatik oynatma
// kurali. Sesli mesajin yaniti kayit bittikten ONLARCA SANIYE sonra geliyor;
// o an kullanici etkilesimi yok ve Safari `play()`i reddediyor. Oge
// tiklama aninda (mikrofon dugmesi) bir kez calistirilip "acilirsa",
// sonradan ayni ogeyle calmaya izin veriliyor.
let shared: HTMLAudioElement | null = null
// Bos (sessiz) WAV - kilit acmak icin. `data:` media-src'de YOK; bu yuzden
// kilit acma sessiz bir `blob:` ile yapiliyor.
const SILENT_WAV = new Uint8Array([
  82, 73, 70, 70, 36, 0, 0, 0, 87, 65, 86, 69, 102, 109, 116, 32, 16, 0, 0, 0, 1, 0, 1, 0, 64, 31,
  0, 0, 64, 31, 0, 0, 1, 0, 8, 0, 100, 97, 116, 97, 0, 0, 0, 0,
])

function sharedAudio(): HTMLAudioElement {
  if (shared === null) shared = new Audio()
  return shared
}

/**
 * Tiklama ANINDA cagrilmali (ornegin mikrofon dugmesi). Ses ogesini sessiz
 * bir dosyayla calistirip otomatik oynatma kilidini acar. Hata yutuluyor:
 * kilit acilamazsa en kotu ihtimalle yanit otomatik okunmaz, kullanici
 * okuma dugmesine basar - hicbir sey bozulmaz.
 */
export function unlockAudio(): void {
  unlockElement(sharedAudio())
}

/** Verilen ses ogesinin otomatik oynatma kilidini acar (tiklama aninda). */
export function unlockElement(audio: HTMLAudioElement): void {
  try {
    const url = URL.createObjectURL(new Blob([SILENT_WAV], { type: 'audio/wav' }))
    audio.src = url
    void audio
      .play()
      .catch(() => undefined)
      .finally(() => URL.revokeObjectURL(url))
  } catch {
    // Ortam ses desteklemiyor (test ortami gibi): yok say.
  }
}

function release(): void {
  finishPlaying?.()
  if (current) {
    current.pause()
    current.removeAttribute('src')
  }
  if (currentUrl) URL.revokeObjectURL(currentUrl)
  current = null
  currentUrl = null
}

// --- Ses tercihi ------------------------------------------------------------
//
// Kullanici karari: Chatterbox (dogal, ilk ses ~2-6 sn) VARSAYILAN; macOS sesi
// ("system", aninda) secilebilir. Tercih bu cihazda (`localStorage`) - sunucu
// tarafi ayar gerekmiyor, her istek secimi `voice` alaniyla tasiyor.

export type TtsVoice = 'chatterbox' | 'system'
const VOICE_KEY = 'hedefai:ttsVoice'

export function getTtsVoice(): TtsVoice {
  try {
    return localStorage.getItem(VOICE_KEY) === 'system' ? 'system' : 'chatterbox'
  } catch {
    return 'chatterbox'
  }
}

export function setTtsVoice(voice: TtsVoice): void {
  try {
    localStorage.setItem(VOICE_KEY, voice)
  } catch {
    // Tercih bir kolaylik; secim bu oturumda varsayilana doner.
  }
}

/** Metni seslendirme parcalarina boler (ilk parca kisa - erken ilk ses). */
export function speechPieces(text: string): string[] {
  const splitter = new SentenceSplitter()
  return [...splitter.push(text), ...splitter.flush()]
}

let speakToken = 0
let finishPlaying: (() => void) | null = null

async function fetchSpeech(text: string, voice: TtsVoice): Promise<Blob> {
  const response = await fetch('/api/tts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ text, voice }),
  })
  if (!response.ok) throw new Error(String(response.status))
  return response.blob()
}

/**
 * Calmaya baslar; `ended` sesin BITISINI (veya kesilmesini) bekler.
 *
 * Nesne icinde donuyor: `async` bir fonksiyondan dogrudan soz dondurmek onu
 * otomatik BEKLETIR - cagiran sesin sonuna kadar takilirdi.
 */
async function startPlayback(blob: Blob): Promise<{ ended: Promise<void> }> {
  // Onceki parcanin nesne URL'i serbest birakiliyor - birakilmazsa her parca
  // bellekte birkac yuz KB birakirdi.
  if (currentUrl) URL.revokeObjectURL(currentUrl)
  const url = URL.createObjectURL(blob)
  const audio = sharedAudio()
  audio.src = url
  current = audio
  currentUrl = url
  const ended = new Promise<void>((resolve) => {
    const done = () => {
      if (finishPlaying === done) finishPlaying = null
      resolve()
    }
    finishPlaying = done
    audio.onended = done
    audio.onerror = done
  })
  await audio.play()
  return { ended }
}

/**
 * Metni sunucuda seslendirip calar; PARCA PARCA.
 *
 * Chatterbox'ta uzun bir yanitin tamamini tek istekte seslendirmek dakikalar
 * surerdi (uretim yaklasik gercek zamanda). Metin cumlelere bolunuyor, ilk
 * parca kisa tutuluyor ve bir parca calarken sonraki hazirlaniyor. Soz, ILK
 * parca calmaya baslayinca cozulur (hata ilk parcada firlar: cagiran tarayici
 * okumasina dusebilsin); geri kalani arka planda surer, bitince `onEnd`.
 *
 * Bir onceki okumayi KESIYOR: iki yanitin ust uste okunmasi anlasilmaz.
 */
export async function speakViaServer(text: string, onEnd?: () => void): Promise<void> {
  release()
  stopSpeaking()
  const token = ++speakToken
  const voice = getTtsVoice()
  const pieces = speechPieces(text)
  if (pieces.length === 0) {
    onEnd?.()
    return
  }
  const first = await fetchSpeech(pieces[0], voice)
  if (token !== speakToken) return
  let next: Promise<Blob | null> | null =
    pieces.length > 1 ? fetchSpeech(pieces[1], voice).catch(() => null) : null
  const { ended: firstEnded } = await startPlayback(first)

  void (async () => {
    await firstEnded
    for (let index = 1; index < pieces.length && token === speakToken; index++) {
      const blob = await next
      if (token !== speakToken || !blob) break
      next = index + 1 < pieces.length ? fetchSpeech(pieces[index + 1], voice).catch(() => null) : null
      try {
        await (await startPlayback(blob)).ended
      } catch {
        break
      }
    }
    if (token === speakToken) {
      release()
      onEnd?.()
    }
  })()
}

/** Sunucu veya tarayici - hangisi caliyorsa durdurur. */
export function stopAllSpeech(): void {
  speakToken++
  release()
  stopSpeaking()
}

// --- Sesli mesajin yanitini otomatik oku ----------------------------------------
//
// Kullanici SESLI mesaj gonderdiyse yanitin da sesli gelmesi dogal akis
// (telefonla konusur gibi). Bayrak modul seviyesinde: yeni bir sohbetin ilk
// mesajinda konusma kimligi gonderim aninda henuz YOK, bu yuzden "bu
// konusmanin yaniti" demek yerine "siradaki tamamlanan yanit" deniyor.

let voiceReplyArmed = false

export function armVoiceReply(): void {
  voiceReplyArmed = true
}

/** Silahli ise bayragi indirip `true` doner (tek seferlik). */
export function consumeVoiceReply(): boolean {
  const armed = voiceReplyArmed
  voiceReplyArmed = false
  return armed
}

/**
 * Sesli mesaj: kayit `/api/attachments`a EK olarak gidiyor. Sunucu yuklemede
 * metne ceviriyor ve transkripti donduruyor - API istemcilerinin kullandigi
 * yolun aynisi, iki yol ayrisamasin. Mikrofon dugmesi ve sesli gorusme ortak.
 */
export async function uploadVoiceMessage(blob: Blob): Promise<{ id: string; transcript: string }> {
  const body = new FormData()
  body.append('file', blob, `sesli-mesaj.${extensionFor(blob.type)}`)
  body.append('language', 'tr')
  const response = await api('/api/attachments', { method: 'POST', body })
  if (!response.ok) throw new Error(String(response.status))
  const data = (await response.json()) as { id: string; transcript?: string | null }
  return { id: data.id, transcript: (data.transcript ?? '').trim() }
}
